import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiHome, FiArrowRight } from "react-icons/fi";
import { t, getLangFromPathname, buildLangPath, normalizeLang, resources } from "../../i18n";
import { useSiteContent } from "../../content/SiteContentContext";

function resolveLang(location) {
  // 1) BrowserRouter: /en/... /es/...
  if (/^\/(en|es)(\/|$)/.test(location.pathname)) {
    return getLangFromPathname(location.pathname);
  }

  // 2) HashRouter tuki: #/en/... / #/es/...
  const hash = window.location.hash || "";
  const hashPath = hash.startsWith("#") ? hash.slice(1) : hash;
  if (/^\/(en|es)(\/|$)/.test(hashPath)) {
    return getLangFromPathname(hashPath);
  }

  // 3) html lang="en|es|fi"
  const htmlLang = (document?.documentElement?.lang || "").trim().slice(0, 2);
  if (htmlLang) {
    return normalizeLang(htmlLang);
  }

  // 4) mahdollinen localStorage-avain (eri toteutuksille fallback)
  const savedLang =
    localStorage.getItem("lang") ||
    localStorage.getItem("i18nLang") ||
    localStorage.getItem("language");

  if (savedLang) {
    return normalizeLang(savedLang);
  }

  // 5) fallback
  return "fi";
}

export default function NotFound({ lang: langProp }) {
  useSiteContent();
  const location = useLocation();
  // App.jsx:n lang on ensisijainen totuus, koska Routes saa stripatun locationin
  // (jolloin useLocation() täällä ei aina sisällä /en tai /es prefiksiä).
  const lang = normalizeLang(langProp || resolveLang(location));

  // DEBUG: selvitetään mistä 404-sivu päättää kielen ja mistä tekstit tulevat
  useEffect(() => {
    const hash = window.location.hash || "";
    const hashPath = hash.startsWith("#") ? hash.slice(1) : hash;
    const htmlLang = (document?.documentElement?.lang || "").trim();

    console.groupCollapsed("[NotFound i18n debug]");
    console.log("pathname:", location.pathname);
    console.log("hash:", hash);
    console.log("hashPath:", hashPath);
    console.log("resolved lang:", lang);
    console.log("html lang:", htmlLang);

    // Tarkista löytyvätkö notFound-avaimet root-tasolta
    console.log("FI notFound.title:", resources?.fi?.notFound?.title);
    console.log("EN notFound.title:", resources?.en?.notFound?.title);
    console.log("ES notFound.title:", resources?.es?.notFound?.title);

    // Tarkista myös mahdollinen väärä nesting (privacyPage.notFound)
    console.log("FI privacyPage.notFound.title:", resources?.fi?.privacyPage?.notFound?.title);
    console.log("EN privacyPage.notFound.title:", resources?.en?.privacyPage?.notFound?.title);
    console.log("ES privacyPage.notFound.title:", resources?.es?.privacyPage?.notFound?.title);

    // Mitä t() oikeasti palauttaa tällä sivulla
    console.log('t("notFound.title", lang):', t("notFound.title", lang));
    console.log('t("notFound.lead", lang):', t("notFound.lead", lang));
    console.log('t("notFound.cta.home", lang):', t("notFound.cta.home", lang));
    console.log('t("notFound.cta.quote", lang):', t("notFound.cta.quote", lang));

    console.groupEnd();
  }, [location.pathname, lang]);

  // Etusivu nykyisellä kielellä: "/", "/en/", "/es/"
  const homePath = buildLangPath(lang, "/");

  // Ankkurit etusivulle nykyisellä kielellä
  const toHomeSection = (id) => `${homePath}#${id}`;

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-white dark:bg-blue-950 overflow-hidden">
      {/* taustavalot */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-lime-400/10 rounded-full blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-3xl text-center px-6">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-lime-500/15 text-lime-500 text-5xl mb-8">
          404
        </div>

        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white">
          {t("notFound.title", lang)}
        </h1>

        <p className="mt-4 text-gray-600 dark:text-gray-300 text-lg max-w-xl mx-auto">
          {t("notFound.lead", lang)}
        </p>

        {/* CTA-napit */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to={homePath}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-lime-500 text-black font-bold shadow-lg hover:shadow-xl transition"
          >
            <FiHome />
            {t("notFound.cta.home", lang)}
          </Link>

          <Link
            to={toHomeSection("contact")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-lime-400 text-gray-900 dark:text-white hover:bg-lime-400 hover:text-black transition"
          >
            <FiArrowRight />
            {t("notFound.cta.quote", lang)}
          </Link>
        </div>

        {/* pieni lisämyynti */}
        <div className="mt-12 text-sm text-gray-500 dark:text-gray-400">
          {t("notFound.popular", lang)}
          <div className="mt-3 flex flex-wrap justify-center gap-3">
            <Link to={toHomeSection("works")} className="underline hover:text-lime-400">
              {t("notFound.links.works", lang)}
            </Link>
            <Link to={toHomeSection("skills")} className="underline hover:text-lime-400">
              {t("notFound.links.skills", lang)}
            </Link>
            <Link to={toHomeSection("about")} className="underline hover:text-lime-400">
              {t("notFound.links.about", lang)}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

